<template>
    <div v-if="showDisconnectedOverlay && !isMqttConnected" class="overlay-banner has-background-info">
      {{ $t('events.streamDisconnected') }}
    </div>
    <div :class="['table-content', { 'is-dimmed': showDisconnectedOverlay && !isMqttConnected }]">
      <!-- Search and Control Bar -->
      <div class="field is-grouped mb-4">
        <div class="control is-expanded">
          <b-input
            type="search"
            :size="size"
            icon="magnify"
            :placeholder="$t('events.searchPlaceholder')"
            v-model="searchQuery"
          />
        </div>
        <div class="control">
          <b-button
            :size="size"
            :class="{ 'is-info': !isPaused, 'is-warning': isPaused }"
            @click="togglePause"
            :title="isPaused ? $t('events.playTooltip') : $t('events.pauseTooltip')"
            :icon-left="isPaused ? 'play' : 'pause'"
          />
        </div>
        <div class="control">
          <b-button
            :size="size"
            class="is-danger" 
            @click="clearEvents"
            :title="$t('events.clearTooltip')"
            icon-left="delete"
          />
        </div>
      </div>
      
      <div class="list-table-container">
      <!-- Events Table -->
      <div class="list-table-container">
        <b-table :data="filteredEvents" striped :loading="isLoading" :scrollable="Boolean(maxHeight)" :max-height="maxHeight" @click="handleRowClick">
        <b-table-column field="topic" :label="$t('events.topic')" width="25%" v-slot="props">
          <div class="ellipsis-cell">{{ props.row.topic }}</div>
        </b-table-column>
        <b-table-column field="message" :label="$t('events.message')" v-slot="props">
          <div class="ellipsis-cell clickable" @click="openMessageModal(props.row)" :title="$t('events.message')">
            {{ props.row.message }}
          </div>
        </b-table-column>
        <b-table-column field="time" :label="$t('events.time')" numeric width="25%" v-slot="props">
          {{ $d(new Date(props.row.time), 'shortSeconds') }}
        </b-table-column>
        <template #empty>
          <div class="has-text-centered mt-4 mb-4">
            {{ filteredEvents.length === 0 && events.length > 0 ? $t('events.noSearchResults') : $t('events.noEvents') }}
          </div>
        </template>
      </b-table>
    </div>
    </div>
  </div>

  <!-- Full message viewer -->
  <b-modal v-model="messageModalOpen" has-modal-card trap-focus :destroy-on-hide="true" aria-role="dialog" :can-cancel="['escape','x','outside']" aria-label="Event Message" close-button-aria-label="Close" aria-modal>
    <template #default="props">
      <div class="modal-card" style="width: 900px; max-width: 95vw;">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ selectedRow ? selectedRow.topic : '' }}
          </p>
          <button class="delete" aria-label="close" @click="props.close"></button>
        </header>
        <section class="modal-card-body">
          <div v-if="selectedMessageIsJson" class="fermi-json-editor message-json-editor">
            <JsonEditorVue
              :modelValue="selectedMessageJson"
              :mode="jsonEditorMode"
              :stringified="false"
              :main-menu-bar="false"
              :navigation-bar="false"
              :status-bar="false"
              :readOnly="true"
              style="height: 60vh;"
            />
          </div>
          <div v-else>
            <pre class="message-pre">{{ selectedMessageText }}</pre>
          </div>
        </section>
        <footer class="modal-card-foot is-justify-content-space-between">
          <b-button @click="messageModalOpen = false">Close</b-button>
          <span class="modal-meta-bottom" v-if="selectedRow">{{ $d(new Date(selectedRow.time), 'shortAll') }}</span>
        </footer>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, PropType, ref, watch, computed } from 'vue'
import JsonEditorVue from 'json-editor-vue'
import { Mode } from 'vanilla-jsoneditor'
import type { MqttClient } from 'mqtt'
import { createMqttClient } from '@/services/mqtt'

interface EventData {
  time: string
  topic: string
  message: string
}

export default defineComponent({
  name: 'EventsStreamTable',
  components: { JsonEditorVue },
  props: {
    topics: { type: Array as PropType<string[]>, required: true },
    stripPrefix: { type: String, required: false, default: '' },
    maxHeight: { type: Number, required: false, default: undefined },
    showDisconnectedOverlay: { type: Boolean, required: false, default: false },
    size: { type: String, required: false, default: '' },
  },
  setup(props) {
    const events = ref<EventData[]>([])
    const mqttClient = ref<MqttClient | null>(null)
    const isLoading = ref(false)
    const isMqttConnected = ref(false)
    const isPaused = ref(false)
    const searchQuery = ref('')

    // Message modal state
    const messageModalOpen = ref(false)
    const selectedRow = ref<EventData | null>(null)
    const selectedMessageText = ref('')
    const selectedMessageJson = ref<Record<string, unknown> | Array<unknown> | null>(null)
    const selectedMessageIsJson = computed(() => selectedMessageJson.value !== null)
    const jsonEditorMode: Mode = Mode.text

    // Search filtering logic
    const filteredEvents = computed(() => {
      if (!searchQuery.value.trim()) {
        return events.value
      }

      const query = searchQuery.value.trim()
      
      // Check for prefixed searches
      if (query.startsWith('topic:')) {
        const searchTerm = query.slice(6).toLowerCase()
        return events.value.filter(event => 
          event.topic.toLowerCase().includes(searchTerm)
        )
      }
      
      if (query.startsWith('data:')) {
        const searchTerm = query.slice(5).toLowerCase()
        return events.value.filter(event => 
          event.message.toLowerCase().includes(searchTerm)
        )
      }
      
      if (query.startsWith('range:')) {
        const rangePart = query.slice(6)
        const rangeMatch = rangePart.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/)
        
        if (rangeMatch) {
          const min = parseFloat(rangeMatch[1])
          const max = parseFloat(rangeMatch[2])
          
          return events.value.filter(event => {
            // Try to parse the message as a number
            const messageValue = parseFloat(event.message)
            return !isNaN(messageValue) && messageValue >= min && messageValue <= max
          })
        }
        
        // If range format is invalid, fall back to general search
        return events.value.filter(event => 
          event.topic.toLowerCase().includes(rangePart.toLowerCase()) ||
          event.message.toLowerCase().includes(rangePart.toLowerCase())
        )
      }
      
      // General search (no prefix) - search both topic and message
      const searchTerm = query.toLowerCase()
      return events.value.filter(event => 
        event.topic.toLowerCase().includes(searchTerm) ||
        event.message.toLowerCase().includes(searchTerm)
      )
    })

    const subscribeAll = () => {
      if (!mqttClient.value) return
      isLoading.value = true
      props.topics.forEach(t => {
        mqttClient.value?.subscribe(t, (err) => {
          isLoading.value = false
          if (err) {
            // eslint-disable-next-line no-console
            console.error('MQTT subscribe error', err)
          }
        })
      })
    }

    const connect = async () => {
      if (mqttClient.value) return
      isLoading.value = true
      mqttClient.value = await createMqttClient({ clientIdPrefix: 'console-app-events', reconnectPeriodMs: 1000, protocolVersion: 5, clean: true })
      if (!mqttClient.value) { isLoading.value = false; return }

      mqttClient.value.on('connect', () => {
        isMqttConnected.value = true
        subscribeAll()
      })
      mqttClient.value.on('reconnect', () => { isMqttConnected.value = false })
      mqttClient.value.on('offline', () => { isMqttConnected.value = false })
      mqttClient.value.on('close', () => { isMqttConnected.value = false })
      mqttClient.value.on('end', () => { isMqttConnected.value = false })
      mqttClient.value.on('error', () => { isMqttConnected.value = false; isLoading.value = false })

      mqttClient.value.on('message', (topic: string, message: unknown) => {
        // Only add new events if not paused
        if (isPaused.value) return
        
        let displayTopic = topic
        if (props.stripPrefix && topic.startsWith(props.stripPrefix)) {
          displayTopic = topic.slice(props.stripPrefix.length)
        }
        events.value.unshift({
          time: new Date().toISOString(),
          topic: displayTopic,
          message: (message as any).toString(),
        })
      })

      // If topics change, resubscribe
      watch(() => props.topics.slice(), () => {
        subscribeAll()
      })
    }

    const togglePause = () => {
      isPaused.value = !isPaused.value
    }

    const clearEvents = () => {
      events.value = []
    }

    const openMessageModal = (row: EventData) => {
      selectedRow.value = row
      selectedMessageText.value = String(row.message || '')
      // Try to parse as JSON; accept objects/arrays only
      try {
        const parsed = JSON.parse(selectedMessageText.value)
        if (parsed !== null && (typeof parsed === 'object')) {
          selectedMessageJson.value = parsed as any
        } else {
          selectedMessageJson.value = null
        }
      } catch {
        selectedMessageJson.value = null
      }
      messageModalOpen.value = true
    }

    const handleRowClick = (row: EventData) => {
      if (row && (row as any).message) {
        openMessageModal(row)
      }
    }

    onMounted(connect)
    onBeforeUnmount(() => { mqttClient.value?.end() })

    return { 
      events, 
      isLoading, 
      isMqttConnected, 
      isPaused, 
      searchQuery, 
      filteredEvents, 
      togglePause, 
      clearEvents,
      // modal
      messageModalOpen,
      selectedRow,
      selectedMessageText,
      selectedMessageJson,
      selectedMessageIsJson,
      jsonEditorMode,
      openMessageModal,
      handleRowClick
    }
  }
})
</script>

<style scoped>
.table-overlay-container {
  position: relative;
}
.overlay-banner {
  position: absolute;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  padding: 6px 12px;
  border-radius: 6px;
  color: #363636;
  box-shadow: 4px 4px 4px rgba(0,0,0,0.1);
}
.table-content.is-dimmed {
  filter: blur(2px);
}
.ellipsis-cell {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ellipsis-cell.clickable {
  cursor: pointer;
}
/* Modal plain text message */
.message-pre {
  white-space: pre-wrap;
  word-break: break-word;
}
/* Hide line numbers/gutters inside the JSON editor for the modal only */
.message-json-editor :deep(.cm-gutters) {
  display: none;
}
/* Ensure columns respect widths so the message column flexes to remaining space */
:deep(.b-table table) {
  table-layout: fixed;
  width: 100%;
}
/* Make table rows look clickable */
:deep(.b-table .table tbody tr) {
  cursor: pointer;
}
.modal-meta {
  margin-left: auto;
  font-size: 0.9em;
  color: var(--text-muted);
}
.modal-meta-bottom {
  margin-top: 12px;
  text-align: right;
  font-size: 0.9em;
  color: var(--text-muted);
}
</style>


